// Per-chain transaction sending functions
// I/O boundary: each function takes wallet provider + TxRequest, returns TxResult
// All functions are async, never throw — errors wrapped in Result
// EVM-only for wagmi direct integration
// Fee routing: fee mode 'fee' routes through the ChainSaleFee contract where deployed; 'direct' (or no
// contract) sends payer → merchant with no fee — switch lives in @shared/fee/feeMode.ts
// Chain pinning: the wallet is switched to the payment's network and every call carries chainId,
// so wagmi refuses to sign on any other chain (ConnectorChainMismatchError)

import type { TxRequest, TxResult } from '../../txRequest.ts';
import { ok, err } from '@shared/types/result.ts';
import type { Result } from '@shared/types/result.ts';
import { calculateFeeBaseUnits } from '@shared/fee/feeCalc.ts';
import { resolveFeeRoute, FEE_MODE } from '@shared/fee/feeMode.ts';
import type { FeeMode } from '@shared/fee/feeMode.ts';
import { NETWORKS } from '@shared/registry/networks.ts';

// --- Minimal ABIs ---

const ERC20_TRANSFER_ABI = [
  {
    type: 'function' as const,
    name: 'transfer' as const,
    stateMutability: 'nonpayable' as const,
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
] as const;

const ERC20_APPROVE_ABI = [
  {
    type: 'function' as const,
    name: 'approve' as const,
    stateMutability: 'nonpayable' as const,
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
] as const;

const FEE_FORWARD_NATIVE_ABI = [
  {
    type: 'function' as const,
    name: 'forwardNative' as const,
    stateMutability: 'payable' as const,
    inputs: [
      { name: 'recipient', type: 'address' },
      { name: 'fee', type: 'uint256' },
    ],
    outputs: [],
  },
] as const;

const FEE_FORWARD_TOKEN_ABI = [
  {
    type: 'function' as const,
    name: 'forwardToken' as const,
    stateMutability: 'nonpayable' as const,
    inputs: [
      { name: 'token', type: 'address' },
      { name: 'recipient', type: 'address' },
      { name: 'amount', type: 'uint256' },
      { name: 'fee', type: 'uint256' },
    ],
    outputs: [],
  },
] as const;

// --- Type aliases for wagmi hook return functions ---

type WagmiSendTx = (args: {
  readonly to: `0x${string}`;
  readonly value: bigint;
  readonly chainId: number;
}) => Promise<`0x${string}`>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WagmiWriteContract = (args: Record<string, any>) => Promise<`0x${string}`>;

type WagmiSwitchChain = (args: { readonly chainId: number }) => Promise<unknown>;

// Payer's current ERC-20 allowance for a spender (owner = connected account)
type ReadAllowance = (args: {
  readonly token: `0x${string}`;
  readonly spender: `0x${string}`;
  readonly chainId: number;
}) => Promise<bigint>;

type WaitForReceipt = (args: {
  readonly hash: `0x${string}`;
  readonly chainId: number;
}) => Promise<Readonly<{ readonly status: 'success' | 'reverted' }>>;

// Wallet I/O the EVM senders need, injected by the wagmi hook
export type EvmSendDeps = Readonly<{
  readonly sendTx: WagmiSendTx;
  readonly writeContract: WagmiWriteContract;
  readonly switchChain: WagmiSwitchChain;
  readonly getChainId: () => number | undefined;
  readonly waitForReceipt: WaitForReceipt;
  readonly readAllowance: ReadAllowance;
}>;

// --- Fee-routed native send ---

const _sendNativeViaFeeContract = async (
  request: TxRequest,
  deps: EvmSendDeps,
  feeContractAddress: string,
  chainId: number,
): Promise<TxResult> => {
  const baseAmount = BigInt(request.amount);
  const { totalToSend, fee } = calculateFeeBaseUnits(
    baseAmount,
    request.amountUsd ?? 0,
    request.decimals,
    request.exchangeRateUsd ?? 0,
  );

  const hash = await deps.writeContract({
    address: feeContractAddress as `0x${string}`,
    abi: FEE_FORWARD_NATIVE_ABI,
    functionName: 'forwardNative',
    args: [request.recipientAddress as `0x${string}`, fee],
    value: totalToSend,
    chainId,
  });
  return ok(hash);
};

// --- Fee-routed token send (approve + forwardToken) ---
// The split is atomic inside forwardToken: cancelling either prompt means nothing is paid. The approve
// moves no funds, and the contract only pulls from its caller, so a leftover allowance can't be used by others.

const _approve = async (
  deps: EvmSendDeps,
  token: `0x${string}`,
  spender: `0x${string}`,
  amount: bigint,
  chainId: number,
): Promise<boolean> => {
  const hash = await deps.writeContract({
    address: token,
    abi: ERC20_APPROVE_ABI,
    functionName: 'approve',
    args: [spender, amount],
    chainId,
  });
  return (await deps.waitForReceipt({ hash, chainId })).status === 'success';
};

// Tokens whose approve() reverts when changing one non-zero allowance to another (must go via 0)
const ZERO_RESET_COINS: ReadonlySet<string> = new Set(['USDT-ETH']);

// Step 1 (Allow): make the fee contract's allowance cover `total`, ONE wallet prompt per call (each
// prompt must come from its own tap on mobile). No prompt when an earlier (e.g. cancelled-at-Pay)
// approval already covers it. A zero-reset token with a smaller leftover allowance gets only the reset
// this call → ok('reset'); the next call approves. Approvals are mined before returning (forwardToken's
// gas estimate reverts without them). ok('approved' | 'already-approved' | 'reset').
const _ensureFeeAllowance = async (
  coinId: string,
  token: `0x${string}`,
  spender: `0x${string}`,
  total: bigint,
  deps: EvmSendDeps,
  chainId: number,
): Promise<TxResult> => {
  const allowance = await deps.readAllowance({ token, spender, chainId });
  if (allowance >= total) return ok('already-approved');
  if (allowance > 0n && ZERO_RESET_COINS.has(coinId)) {
    return (await _approve(deps, token, spender, 0n, chainId))
      ? ok('reset')
      : err('Token approval reset failed on-chain');
  }
  if (!(await _approve(deps, token, spender, total, chainId))) {
    return err('Token approval failed on-chain');
  }
  return ok('approved');
};

const _sendTokenViaFeeContract = async (
  request: TxRequest,
  deps: EvmSendDeps,
  feeContractAddress: string,
  chainId: number,
): Promise<TxResult> => {
  if (!request.contractAddress) return err('Token contract address missing');

  const baseAmount = BigInt(request.amount);
  const { totalToSend, fee } = calculateFeeBaseUnits(
    baseAmount,
    request.amountUsd ?? 0,
    request.decimals,
    request.exchangeRateUsd ?? 0,
  );

  const token = request.contractAddress as `0x${string}`;
  const spender = feeContractAddress as `0x${string}`;

  // Step 1 normally ran already (separate Allow tap — approveEvmFeeSpend); this is the fallback.
  // A zero-reset token may need a second call (reset, then approve).
  const first = await _ensureFeeAllowance(request.coinId, token, spender, totalToSend, deps, chainId);
  if (!first.ok) return first;
  if (first.value === 'reset') {
    const second = await _ensureFeeAllowance(request.coinId, token, spender, totalToSend, deps, chainId);
    if (!second.ok) return second;
  }

  // Step 2: Call forwardToken on fee contract
  const hash = await deps.writeContract({
    address: feeContractAddress as `0x${string}`,
    abi: FEE_FORWARD_TOKEN_ABI,
    functionName: 'forwardToken',
    args: [
      request.contractAddress as `0x${string}`,
      request.recipientAddress as `0x${string}`,
      totalToSend,
      fee,
    ],
    chainId,
  });
  return ok(hash);
};

// --- Direct send (no fee) ---

const _sendNativeDirect = async (
  request: TxRequest,
  deps: EvmSendDeps,
  chainId: number,
): Promise<TxResult> => {
  const hash = await deps.sendTx({
    to: request.recipientAddress as `0x${string}`,
    value: BigInt(request.amount),
    chainId,
  });
  return ok(hash);
};

const _sendTokenDirect = async (
  request: TxRequest,
  deps: EvmSendDeps,
  chainId: number,
): Promise<TxResult> => {
  if (!request.contractAddress) return err('Token contract address missing');
  const hash = await deps.writeContract({
    address: request.contractAddress as `0x${string}`,
    abi: ERC20_TRANSFER_ABI,
    functionName: 'transfer',
    args: [request.recipientAddress as `0x${string}`, BigInt(request.amount)],
    chainId,
  });
  return ok(hash);
};

// --- EVM (native ETH + ERC-20 tokens) ---

// Resolve the payment's EVM chain and switch the wallet to it (wallets sign on their active chain)
const _prepareChain = async (request: TxRequest, deps: EvmSendDeps): Promise<Result<number>> => {
  const network = NETWORKS[request.networkId];
  if (network === undefined || network.chainFamily !== 'evm') return err(`Not an EVM network: ${request.networkId}`);
  if (deps.getChainId() !== network.chainId) await deps.switchChain({ chainId: network.chainId });
  return ok(network.chainId);
};

// Allow step for fee-routed token payments, run from its own tap. Mobile browsers only open the wallet
// app from a user gesture, so an automatic second request after the approve never surfaced (field test
// 2026-10-25): Allow and Pay are separate taps. ok('approved' | 'already-approved' | 'reset' | 'not-needed')
// — 'reset' means tap Allow once more (Ethereum USDT leftover allowance went to 0 first).
export const approveEvmFeeSpend = async (
  request: TxRequest,
  deps: EvmSendDeps,
  feeMode: FeeMode = FEE_MODE,
): Promise<TxResult> => {
  const feeContract = resolveFeeRoute(request.networkId, feeMode);
  if (feeContract === null || request.coinKind !== 'token' || !request.contractAddress) return ok('not-needed');
  const chain = await _prepareChain(request, deps);
  if (!chain.ok) return chain;
  const { totalToSend } = calculateFeeBaseUnits(
    BigInt(request.amount), request.amountUsd ?? 0, request.decimals, request.exchangeRateUsd ?? 0,
  );
  return _ensureFeeAllowance(
    request.coinId, request.contractAddress as `0x${string}`, feeContract as `0x${string}`, totalToSend, deps, chain.value,
  );
};

export const sendEvmTransaction = async (
  request: TxRequest,
  deps: EvmSendDeps,
  feeMode: FeeMode = FEE_MODE,
): Promise<TxResult> => {
  const chain = await _prepareChain(request, deps);
  if (!chain.ok) return chain;
  const chainId = chain.value;

  const feeContract = resolveFeeRoute(request.networkId, feeMode);

  if (feeContract) {
    // Route through fee contract
    return request.coinKind === 'native'
      ? _sendNativeViaFeeContract(request, deps, feeContract, chainId)
      : _sendTokenViaFeeContract(request, deps, feeContract, chainId);
  }

  // Direct send — fee mode 'direct', or no fee contract on this network
  return request.coinKind === 'native'
    ? _sendNativeDirect(request, deps, chainId)
    : _sendTokenDirect(request, deps, chainId);
};

// Re-export types for external use
export type { WagmiSendTx, WagmiWriteContract, WagmiSwitchChain, WaitForReceipt, ReadAllowance };
